import type { NavItem } from "@shared/types/navigation"
import type { UserRole } from "@/features/auth/services/session.service"
import type { MerchantService } from "@shared/types/merchant"
import {
  ArrowLeftRight,
  BarChart3,
  Building2,
  CreditCard,
  Home,
  Landmark,
  Plug,
  Radar,
  Receipt,
  ShieldCheck,
  Sparkles,
  Store,
  Tag,
  TrendingUp,
  User,
  Users,
  Wallet,
  Zap,
  Banknote,
} from "lucide-react"

/* ------------------------------------------------------------------ */
/*  Merchant navigation                                                */
/* ------------------------------------------------------------------ */

const merchantMain: NavItem[] = [
  { title: "Home", icon: Home, url: "/" },
  { title: "Balance", icon: Banknote, url: "/balance" },
  { title: "Balance Hub", icon: Wallet, url: "/balance-hub" },
  { title: "Transactions", icon: ArrowLeftRight, url: "/payments" },
  { title: "Customers", icon: User, url: "/customers" },
  { title: "Products", icon: Tag, url: "/products" },
]

const merchantServices: NavItem[] = [
  {
    title: "Risk",
    icon: Radar,
    url: "/risk",
    children: [
      { title: "Sonar", url: "/sonar", requiredService: "sonar" },
      { title: "Refunds", url: "/refunds" },
      { title: "Disputes", url: "/disputes" },
    ],
  },
  {
    title: "Engagement",
    icon: Sparkles,
    url: "/engagement",
    requiredService: "engagement",
    children: [
      { title: "Overview", url: "/engagement" },
      { title: "Campaigns", url: "/engagement/campaigns", matchPaths: ["/engagement/campaigns"] },
      { title: "Rewards", url: "/engagement/rewards", matchPaths: ["/engagement/rewards"] },
      { title: "Points", url: "/engagement/points", matchPaths: ["/engagement/points"] },
      { title: "Tiers", url: "/engagement/tiers", matchPaths: ["/engagement/tiers"] },
      { title: "Enrollment", url: "/engagement/enrollment" },
      { title: "Analytics", url: "/engagement/analytics" },
      { title: "Competitive", url: "/engagement/competitive" },
    ],
  },
  {
    title: "POS",
    icon: Store,
    url: "/pos",
    requiredService: "pos",
    children: [
      { title: "Overview", url: "/pos" },
      { title: "Menus", url: "/pos/menus", matchPaths: ["/pos/menus"] },
      { title: "Recipes", url: "/pos/recipes", matchPaths: ["/pos/recipes"] },
      { title: "Inventory", url: "/pos/inventory", matchPaths: ["/pos/inventory"] },
      { title: "Schedule", url: "/pos/schedule", matchPaths: ["/pos/schedule"] },
    ],
  },
  {
    title: "Payments",
    icon: CreditCard,
    url: "/payments-channels",
    children: [
      { title: "In-store", url: "/in-stores", requiredService: "pos" },
      { title: "Ecommerce", url: "/ecommerce", requiredService: "ecommerce" },
      { title: "Links", url: "/payment-links", requiredService: "payment-links" },
    ],
  },
  {
    title: "Advance Services",
    icon: TrendingUp,
    url: "/payments-channels/performance",
    children: [
      {
        title: "Performance",
        url: "/payments-channels/performance",
        requiredService: "performance",
        matchPaths: [
          "/payments-channels/approvals",
          "/payments-channels/declines",
          "/payments-channels/chargebacks",
          "/payments-channels/disputes",
          "/payments-channels/refunds",
          "/payments-channels/alerts",
        ],
      },
      { title: "Cost", url: "/payments-channels/cost", requiredService: "cost" },
      { title: "Opportunities", url: "/payments-channels/opportunities", requiredService: "opportunities" },
      { title: "BINs", url: "/payments-channels/bins", requiredService: "bins" },
      { title: "Tokens", url: "/payments-channels/tokens", requiredService: "tokens" },
    ],
  },
  {
    title: "Billing",
    icon: Receipt,
    url: "/billing",
    requiredService: "billing",
    children: [
      { title: "Overview", url: "/billing/revenue" },
      { title: "Invoices", url: "/invoices" },
      { title: "Quotes", url: "/quotes" },
      { title: "Billing schedules", url: "/billing-schedules" },
      { title: "Usage-based", url: "/usage-billing" },
      { title: "Subscriptions", url: "/subscriptions" },
      { title: "Revenue Recognition", url: "/revenue-recognition" },
    ],
  },
  {
    title: "Reporting",
    icon: BarChart3,
    url: "/reporting",
    children: [
      { title: "Reports", url: "/reporting/reports" },
      { title: "Pipelines", url: "/reporting/pipelines" },
    ],
  },
  {
    title: "Integrations",
    icon: Plug,
    url: "/integrations",
  },
]

/* ------------------------------------------------------------------ */
/*  Bank navigation                                                    */
/* ------------------------------------------------------------------ */

const bankMain: NavItem[] = [
  { title: "Home", icon: Home, url: "/" },
  { title: "Transactions", icon: ArrowLeftRight, url: "/payments" },
  { title: "Merchants", icon: Building2, url: "/merchants" },
]

const bankServices: NavItem[] = [
  {
    title: "Channels",
    icon: Store,
    url: "/channels",
    children: [
      { title: "In-Stores", url: "/in-stores" },
      { title: "Ecommerce", url: "/ecommerce" },
      { title: "Links", url: "/payment-links" },
    ],
  },
  {
    title: "Risk",
    icon: Radar,
    url: "/risk",
    children: [
      { title: "Sonar", url: "/sonar" },
      { title: "MRE", url: "/risk-scorecards" },
      { title: "Refunds", url: "/refunds" },
      { title: "Disputes", url: "/disputes" },
    ],
  },
  {
    title: "Compliance",
    icon: ShieldCheck,
    url: "/compliance/konar",
    children: [
      { title: "Konar", url: "/compliance/konar" },
      { title: "Scorecards", url: "/compliance/scorecards" },
      { title: "Verifications", url: "/compliance/verifications" },
      { title: "SAR", url: "/compliance/sar" },
    ],
  },
  {
    title: "Money Movement",
    icon: Banknote,
    url: "/money-movement/balance",
    children: [
      { title: "Balance", url: "/money-movement/balance" },
      { title: "Accounts", url: "/money-movement/accounts" },
      { title: "Operations", url: "/money-movement/operations" },
      { title: "System Accounts", url: "/money-movement/system-accounts" },
    ],
  },
  {
    title: "Advance Services",
    icon: TrendingUp,
    url: "/payments-channels/performance",
    children: [
      {
        title: "Performance",
        url: "/payments-channels/performance",
        matchPaths: [
          "/payments-channels/approvals",
          "/payments-channels/declines",
          "/payments-channels/chargebacks",
          "/payments-channels/disputes",
          "/payments-channels/refunds",
          "/payments-channels/alerts",
        ],
      },
      { title: "Cost", url: "/payments-channels/cost" },
      { title: "Opportunities", url: "/payments-channels/opportunities" },
      { title: "BINs", url: "/payments-channels/bins" },
      { title: "Tokens", url: "/payments-channels/tokens" },
    ],
  },
  {
    title: "Programs",
    icon: Zap,
    url: "/programs",
  },
  {
    title: "Members",
    icon: Users,
    url: "/members",
  },
  {
    title: "Billing",
    icon: Receipt,
    url: "/billing-admin",
  },
  {
    title: "Rails",
    icon: Landmark,
    url: "/rails",
    children: [
      { title: "Overview", url: "/rails/overview" },
      { title: "Card Rails", url: "/rails/card-rails" },
    ],
  },
  {
    title: "Reporting",
    icon: BarChart3,
    url: "/reporting",
    children: [
      { title: "Reports", url: "/reporting/reports" },
      { title: "Pipelines", url: "/reporting/pipelines" },
    ],
  },
]

/* ------------------------------------------------------------------ */
/*  Public API                                                         */
/* ------------------------------------------------------------------ */

export interface NavigationConfig {
  main: NavItem[]
  services: NavItem[]
  servicesLabel: string
}

/**
 * Filter merchant nav items by the merchant's enabled services.
 * Items without a requiredService are always visible.
 * Top-level items with a requiredService are removed when the service is off.
 * Children are filtered individually; if all children are removed the parent is
 * also removed (unless the parent itself has no requiredService — e.g. Reporting).
 */
function filterByServices(items: NavItem[], enabledServices: MerchantService[]): NavItem[] {
  return items.reduce<NavItem[]>((acc, item) => {
    // Top-level item blocked by its own requiredService
    if (item.requiredService && !enabledServices.includes(item.requiredService)) {
      return acc
    }

    if (item.children) {
      const visibleChildren = item.children.filter(
        (child) => !child.requiredService || enabledServices.includes(child.requiredService)
      )

      // If every child required a service and none are enabled, hide the parent too
      const allChildrenGated = item.children.every((c) => c.requiredService)
      if (allChildrenGated && visibleChildren.length === 0) {
        return acc
      }

      acc.push({ ...item, children: visibleChildren })
      return acc
    }

    acc.push(item)
    return acc
  }, [])
}

export function getNavigationConfig(
  role: UserRole,
  enabledServices?: MerchantService[]
): NavigationConfig {
  if (role === "bank") {
    return { main: bankMain, services: bankServices, servicesLabel: "Operations" }
  }

  const services =
    enabledServices !== undefined
      ? filterByServices(merchantServices, enabledServices)
      : merchantServices

  return { main: merchantMain, services, servicesLabel: "Products" }
}

/** Default (merchant) kept for backwards compatibility */
export const navigationConfig = getNavigationConfig("merchant")
